import React from "react";
import { createSelector } from "@reduxjs/toolkit";
import { flow, isEmpty, orderBy, memoize } from "lodash";
import { format, lastDayOfMonth, lastDayOfYear, subMonths, subYears } from "date-fns";
import {
  FsisFlocReport,
  FPSISSubtotal,
  FSISReport,
  FsisReportTotal,
  FundDates,
  FundDocumentsRow,
  FundGeneralInfoItem,
  FundsState,
  InvestmentScheduleSelectorOptions,
  KeyDatesRows,
  MoicMeta,
  WaterfallTermsRows,
} from "@funds/models";
import { RootState } from "@store/index";
import { CurrencyName, CurrencySymbol } from "@shared/models/currency";
import DateUtils from "@shared/utils/DateUtils";
import { QuarterLabel, QuarterValue } from "@shared/models/date";
import { SelectedOption, Year, BaseRow, RowType } from "@shared/components";
import NumberUtils from "@shared/utils/NumberUtils";
import { entitledFundsSelector, documentTypesSelector } from "@store/Entities/selectors";
import CommonUtils from "@shared/utils/CommonUtils";
import { DocumentStatus } from "@documents/models/filter.models";
import { Nullable } from "@shared/models/general";
import { Fund } from "@store/Entities/models";
import { GeographyName } from "@shared/models/geography";
import { NumberFormat } from "@user/models";
import { preferencesSelector } from "@app/store/selectors";
import { ExcludedFundFamilies, ExcludedSegments } from "@shared/models/filters";
import { formatNumber } from "../../../util/numberTransforms";

export const selectedFundDates = (state: RootState): Nullable<FundDates> => state.funds.fundDates.data;

export const selectedFsisDates = (state: RootState): FundsState["fsisDates"] => state.funds.fsisDates;

export const selectedFsisGrouped = (state: RootState): FundsState["fsisGrouped"] => state.funds.fsisGrouped;

export const selectedHistoricMoic = (state: RootState): FundsState["historicMoic"] => state.funds.historicMoic;
export const selectFSISAppreciation = (state: RootState): FundsState["fundFSISAppreciation"] =>
  state.funds.fundFSISAppreciation;

export const selectedSummaryInvestmentScheduleFilters = (
  state: RootState
): FundsState["summaryInvestmentScheduleFilters"] => state.funds.summaryInvestmentScheduleFilters;

export const selectedFundDocumentsFilters = (state: RootState): FundsState["fundDocumentsFilters"] =>
  state.funds.fundDocumentsFilters;

export const fundDocumentsSelector = (state: RootState): FundsState["fundDocuments"] => state.funds.fundDocuments;

export const fundDocumentsFilterOptionsSelector = (state: RootState): FundsState["fundDocumentsFilterOptions"] =>
  state.funds.fundDocumentsFilterOptions;

export const selectedDocumentTypesFundDocumentsSelector = (
  state: RootState
): FundsState["fundDocumentsFilters"]["documentTypes"] => state.funds.fundDocumentsFilters.documentTypes;

export const selectedInvestorAccountsFundDocumentsSelector = (
  state: RootState
): FundsState["fundDocumentsFilters"]["investorAccounts"] => state.funds.fundDocumentsFilters.investorAccounts;

export const investorAccountOptionsFundDocumentsSelector = (
  state: RootState
): FundsState["fundDocumentsFilterOptions"]["investorAccounts"] =>
  state.funds.fundDocumentsFilterOptions.investorAccounts;
export const fsisExportLoadingSelector = (state: RootState): FundsState["isExportFsisReportLoading"] =>
  state.funds.isExportFsisReportLoading;

export const preparedDocumentTypesFundDocumentsSelector = createSelector(
  documentTypesSelector,
  selectedDocumentTypesFundDocumentsSelector,
  fundDocumentsSelector,
  (documentTypesSlice, selectedDocumentTypeIds) => {
    const selectedDocumentTypeIdsMap = CommonUtils.convertArrayToMap(selectedDocumentTypeIds);

    return orderBy(
      CommonUtils.convertMapToArray(documentTypesSlice.data, (documentType) => ({
        id: documentType.documentTypeId,
        label: documentType.displayName ?? documentType.name,
        selected: selectedDocumentTypeIdsMap[documentType.documentTypeId],
      })),
      [({ label }) => label.toLowerCase()]
    );
  }
);
export const preparedInvestorAccountsFunDocumentSelector = createSelector(
  investorAccountOptionsFundDocumentsSelector,
  selectedInvestorAccountsFundDocumentsSelector,
  (investorAccountsSlice, selectedInvestorAccountIds) => {
    const selectedInvestorAccountIdsMap = CommonUtils.convertArrayToMap(selectedInvestorAccountIds);

    return (investorAccountsSlice.data ?? []).map((ia) => ({
      ...ia,
      selected: selectedInvestorAccountIdsMap[ia.id],
    }));
  }
);

export const fundDocumentsMetaSelector = createSelector(
  documentTypesSelector,
  investorAccountOptionsFundDocumentsSelector,
  (documentTypesSlice, investorAccountsSlice) => ({
    loading: documentTypesSlice.loading || investorAccountsSlice.loading,
    error: documentTypesSlice.error || investorAccountsSlice.error,
    // add meta of docs request itself here
  })
);

export const fundDocumentsFilterSelector = createSelector(
  selectedFundDocumentsFilters,
  fundDocumentsFilterOptionsSelector,
  preparedDocumentTypesFundDocumentsSelector,
  preparedInvestorAccountsFunDocumentSelector,
  fundDocumentsMetaSelector,
  (values, options, documentTypes, investorAccounts, { loading, error }) => ({
    loading,
    error,
    documentTypes,
    investorAccounts,
    selectedInvestorAccountIds: values.investorAccounts,
    selectedDocumentTypeIds: values.documentTypes,
    selectedTimeRange: values.timeRange,
    selectedStatus: values.status,
    fileName: values.fileName,
    timeRangeOptions: options.timeOptions,
    statusOptions: options.statuses,
  })
);

export const sortedFsisDateOptionsSelector = createSelector(
  selectedFsisDates,
  ({ data: fsisDates }): Nullable<Year[]> => {
    if (fsisDates === null) {
      return null;
    }

    const options = fsisDates.reduce((result: Record<string, Year>, date) => {
      const { year, quarter } = DateUtils.convertDateToYearQuarter(new Date(date));
      const yearLabel = String(year);
      const quarterLabel = QuarterLabel[QuarterValue[quarter] as keyof typeof QuarterLabel];

      return {
        ...result,
        [yearLabel]: {
          id: year,
          label: yearLabel,
          quarters: [
            ...(result?.[yearLabel]?.quarters ?? []),
            {
              id: quarter,
              label: quarterLabel,
            },
          ],
        },
      };
    }, {});

    return Object.keys(options)
      .sort()
      .reverse()
      .map((year) => options[year]);
  }
);

export const latestFsisAsOfDateOptionSelector = createSelector(
  selectedFsisDates,
  ({ data: fsisDates }): Nullable<SelectedOption> => {
    const latestDate = fsisDates?.slice().sort().pop();

    return latestDate ? DateUtils.convertDateToYearQuarter(new Date(latestDate)) : null;
  }
);

export const selectedFsisDateSelector = createSelector(
  selectedFsisDates,
  selectedSummaryInvestmentScheduleFilters,
  ({ data: fsisDates }, { asOfDate }): Nullable<string> => {
    if (fsisDates === null || asOfDate === null) {
      return null;
    }

    return (
      fsisDates.find((date) => {
        const { year, quarter } = DateUtils.convertDateToYearQuarter(new Date(date));

        return year === asOfDate.year && quarter === asOfDate.quarter;
      }) ?? null
    );
  }
);

export const selectedFsisReportSelector = createSelector(
  selectedFsisGrouped,
  selectedSummaryInvestmentScheduleFilters,
  selectedFsisDateSelector,
  (_state: RootState, fundId: string) => fundId,
  ({ data: grouped }, { asOfDate }, date, fundId): Nullable<FSISReport> => {
    if (date === null || asOfDate === null) {
      return null;
    }

    const investments = grouped?.[fundId]?.[date]?.investments ?? null;

    if (!investments?.length) {
      return null;
    }

    return (
      investments.find(({ fund, reportingDate }) => {
        const { year, quarter } = DateUtils.convertDateToYearQuarter(new Date(reportingDate));

        return fund === fundId && year === asOfDate.year && quarter === asOfDate.quarter;
      }) ?? null
    );
  }
);

export const fundDatesSelector = createSelector(
  selectedFundDates,
  preferencesSelector,
  (fundPeriods, { DateFormat }) => {
    return fundPeriods ? [`As of ${DateUtils.formatDate(new Date(fundPeriods[0]), DateFormat)}`] : [];
  }
);

export const selectedFundFPSIS = (state: RootState): FundsState["fundFPSIS"] => state.funds.fundFPSIS;

export const fundFPSISSelector = createSelector(
  (state: RootState): FundsState["fundFPSIS"] => selectedFundFPSIS(state),
  ({ data }) => {
    let remainingTotalValue = 0;

    const industries =
      data &&
      data.reduce((acc, item) => {
        if (item.industry) {
          remainingTotalValue += item.remainingFmv;

          if (acc[item.industry]) {
            acc[item.industry] += item.remainingFmv;
          } else {
            acc[item.industry] = item.remainingFmv;
          }
        }

        return acc;
      }, {} as { [key: string]: number });

    return industries
      ? Object.entries(industries)
          .map(([name, value]) => ({
            name,
            value: Number(((100 * value) / remainingTotalValue).toFixed(1)),
          }))
          .filter((datum) => datum.value)
      : [];
  }
);

export const fundHeadsSelector = (state: RootState): FundsState["fundHeads"] => state.funds.fundHeads;

export const selectedFundSelector = (state: RootState, id: string): Nullable<Fund> =>
  state.entities.funds.data ? state.entities.funds.data[id] : null;

// export const selectedFundsWithEntityType = ()

export const generalFundInfoSelector = createSelector(
  (state: RootState, id: string) => selectedFundSelector(state, id),
  preferencesSelector,
  (selectedFund, { DateFormat, NumberFormat }): Nullable<FundGeneralInfoItem[]> => {
    if (!selectedFund) return null;

    const {
      fundStatus,
      currency,
      geography,
      strategy,
      grossIrr,
      netIrr,
      grossIrrPPM,
      netIrrPPM,
      vintage,
      irrAsOfDate,
      size,
      assetClass,
    } = selectedFund;

    const generalInfo: FundGeneralInfoItem[] = [
      { label: "Fund Segment", value: [{ text: assetClass || "" }] },
      {
        label: "Currency",
        value: [{ text: CurrencyName[currency as keyof typeof CurrencyName] || "" }],
      },
      { label: "Vintage", value: [{ text: vintage ? String(vintage) : "" }] },
      { label: "Strategy", value: [{ text: strategy || "" }] },
      { label: "Status", value: [{ text: fundStatus || "" }] },
      {
        label: "Geography",
        value: [{ text: GeographyName[geography as keyof typeof GeographyName] || "" }],
      },
      {
        label: "Fund Performance",
        subLabel: `(as of ${DateUtils.formatDate(new Date(irrAsOfDate), DateFormat)})`,
        value: [
          {
            text:
              grossIrr && grossIrrPPM !== "NM"
                ? `Gross IRR - ${formatNumber(grossIrr * 100, NumberFormat, 1)}%`
                : "Gross IRR - NM",
            tooltip:
              'Gross Internal Rate of Return ("Gross IRR")  represents the annualized IRR for the period indicated on aggregate Limited Partner invested capital based on contributions, distributions and unrealized value before management fees, expenses and carried interest.',
          },
          {
            text:
              netIrr && netIrrPPM !== "NM"
                ? `Net IRR - ${formatNumber(netIrr * 100, NumberFormat, 1)}%`
                : "Net IRR - NM",
            tooltip:
              'Net Internal Rate of Return ("Net IRR") represents the annualized IRR for the period indicated on aggregate Limited Partner invested capital based on all contributions, distributions and unrealized value including management fees, expenses and carried interest. ',
          },
        ],
      },
      {
        label: "Total Fund Size",
        value: [
          {
            text: `${CurrencySymbol[currency as keyof typeof CurrencySymbol]}${formatNumber(size, NumberFormat)}`,
          },
        ],
      },
    ];

    return generalInfo;
  }
);

export const openEndedFundInfoSelector = createSelector(
  (state: RootState, id: string) => selectedFundSelector(state, id),
  preferencesSelector,
  (selectedFund, { NumberFormat }): Nullable<FundGeneralInfoItem[]> => {
    if (!selectedFund) return null;

    const { fundStatus, currency, geography, strategy, vintage, size, assetClass } = selectedFund;

    const generalInfo: FundGeneralInfoItem[] = [
      { label: "Fund Segment", value: [{ text: assetClass || "" }], visible: "true" },
      {
        label: "Currency",
        value: [{ text: CurrencyName[currency as keyof typeof CurrencyName] || "" }],
        visible: "true",
      },
      { label: "Inception", value: [{ text: vintage ? String(vintage) : "" }], visible: "true" },
      { label: "Strategy", value: [{ text: strategy || "" }], visible: "true" },
      { label: "Status", value: [{ text: fundStatus || "" }], visible: "true" },
      {
        label: "Geography",
        value: [{ text: GeographyName[geography as keyof typeof GeographyName] || "" }],
        visible: "true",
      },
      {
        label: "Total Fund Size",
        value: [
          {
            text: `${CurrencySymbol[currency as keyof typeof CurrencySymbol]}${formatNumber(size, NumberFormat)}`,
          },
        ],
        visible: "true",
      },
    ];

    return generalInfo;
  }
);

export const selectedFundsEntityType = createSelector(
  (state: RootState, id: string) => selectedFundSelector(state, id),
  entitledFundsSelector,
  (selectedFund, fundsSlice) => {
    if (!selectedFund || !fundsSlice) return null;

    const { id } = selectedFund;

    // const fundEntityType = fundsSlice.data ? fundsSlice.data[id] : null;
    const funds = CommonUtils.convertMapToArray(fundsSlice.data, (fund) => ({
      ...fund,
    }));
    const entityType = funds.filter((fund) => fund.globalId === id);

    return entityType[0];
  }
);

export const fundSelectors = createSelector(
  fundHeadsSelector,
  fundDatesSelector,
  selectedFundDates,
  selectedFundFPSIS,
  (state: RootState, id: string) => selectedFundSelector(state, id),
  (state: RootState, id: string) => selectedFundsEntityType(state, id),
  generalFundInfoSelector,
  openEndedFundInfoSelector,
  fundFPSISSelector,
  (
    fundHeads,
    fundDates,
    selectedDates,
    selectedFPSIS,
    selectedFund,
    entityFund,
    generalInfo,
    openEndedInfo,
    fundFPSIS
  ) => ({
    fundHeads,
    fundDates,
    selectedFPSIS,
    selectedDates,
    selectedFund,
    entityFund,
    generalInfo,
    openEndedInfo,
    fundFPSIS,
  })
);

export const keyDatesDataGridSelector = createSelector(
  (state: RootState, id: string) => selectedFundSelector(state, id),
  preferencesSelector,
  (selectedFund, { DateFormat }) => {
    if (!selectedFund) return null;

    const {
      investmentPeriodOriginal,
      investmentPeriodCurrent,
      followOnPeriodOriginal,
      followOnPeriodCurrent,
      termExpirationOriginal,
      termExpirationCurrent,
    } = selectedFund;

    const getFormattedDate = (date: string): string => (date ? DateUtils.formatDate(new Date(date), DateFormat) : "");

    return [
      {
        fieldDescriptor: KeyDatesRows.InvestmentPeriod,
        original: getFormattedDate(investmentPeriodOriginal),
        current: getFormattedDate(investmentPeriodCurrent),
      },
      {
        fieldDescriptor: KeyDatesRows.FollowOnPeriod,
        original: getFormattedDate(followOnPeriodOriginal),
        current: getFormattedDate(followOnPeriodCurrent),
      },
      {
        fieldDescriptor: KeyDatesRows.TermExpiration,
        original: getFormattedDate(termExpirationOriginal),
        current: getFormattedDate(termExpirationCurrent),
      },
    ];
  }
);

export const WaterfallTermsDataGridSelector = createSelector(
  (state: RootState, id: string) => selectedFundSelector(state, id),
  preferencesSelector,
  (selectedFund, { NumberFormat }) => {
    if (!selectedFund) return null;

    const { catchUpSplitByGpLp, hurdleRatePct, carriedIntPct } = selectedFund;

    const getFormattedNumber = (number: number): string => (number ? `${formatNumber(number, NumberFormat, 2)}%` : "");

    return [
      {
        fieldDescriptor: WaterfallTermsRows.InterestCatchUpRate,
        fieldData: catchUpSplitByGpLp,
      },
      {
        fieldDescriptor: WaterfallTermsRows.PreferredReturn,
        fieldData: getFormattedNumber(hurdleRatePct),
      },
      {
        fieldDescriptor: WaterfallTermsRows.CarriedInterestRate,
        fieldData: getFormattedNumber(carriedIntPct),
      },
    ];
  }
);

export const fsisGroupedSelector = createSelector(
  selectedFsisGrouped,
  selectedFsisDateSelector,
  (_state: RootState, options: { fundId: string }) => options.fundId,
  ({ data }, date, fundId) => {
    if (!date || !data?.[fundId]?.[date]) {
      return null;
    }

    return data[fundId][date];
  }
);

export const fsisNotePositionsSelector = createSelector(
  fsisGroupedSelector,
  (_state: RootState, options: { fundId: string }) => options.fundId,
  (grouped): Nullable<Record<string, { position: number; notes: string }>> => {
    const groupedData = grouped?.groupedData;
    const flocData = grouped?.flocData;

    if (
      !groupedData?.realized?.reports ||
      !groupedData?.unrealized?.reports ||
      !groupedData?.publiclyTraded?.reports ||
      !groupedData?.adjustments?.reports ||
      !flocData?.firstThird ||
      !flocData?.secondFourth
    ) {
      return null;
    }

    const reports = [
      ...groupedData.realized.reports,
      ...groupedData.unrealized.reports,
      ...groupedData.publiclyTraded.reports,
      ...groupedData.adjustments.reports,
    ];
    const flocReports = [...flocData.firstThird, ...flocData.secondFourth];

    const noteReports = reports.filter(({ notes }) => !!notes);
    const flocNoteReports = flocReports.filter(({ notes }) => !!notes);

    const fsisNotes = noteReports.reduce<Record<string, { position: number; notes: string }>>(
      (result, { companyName, notes }, index) => ({
        ...result,
        [companyName]: { position: index + 4, notes: notes ?? "" },
      }),
      {}
    );

    const flocNotes = flocNoteReports.reduce<Record<string, { position: number; notes: string }>>(
      (result, { adjustmentTypeValue, notes }, index) => ({
        ...result,
        [adjustmentTypeValue]: {
          position: noteReports.length + index + 4,
          notes: notes ?? "",
        },
      }),
      {}
    );

    return { ...fsisNotes, ...flocNotes };
  }
);

type CombinedSelectorProps = {
  year: number;
  quarter: number;
  assetClass: string;
  strategy: string;
  reportStatus: string;
};

// Helper functions for flag calculations
export const calculateIsQ42025OrAfter = (year: number, quarter: number): boolean => {
  return (year === 2025 && quarter >= QuarterValue.Fourth) || year > 2025;
};

export const calculateIsFundGPEExcludingRealEstate = (assetClass: string, strategy: string): boolean => {
  return !ExcludedSegments.includes(assetClass) && !ExcludedFundFamilies.includes(strategy);
};

// Reusable selectors for flags
export const isQ42025OrAfterFromDateSelector = createSelector(selectedFsisDateSelector, (date): boolean => {
  if (date === null) {
    return false;
  }
  const { year, quarter } = DateUtils.convertDateToYearQuarter(new Date(date));

  return calculateIsQ42025OrAfter(year, quarter);
});

export const isFundGPEExcludingRealEstateSelector = createSelector(
  (_state: RootState, options: InvestmentScheduleSelectorOptions) => options,
  (options): boolean => {
    return calculateIsFundGPEExcludingRealEstate(options.assetClass, options.strategy);
  }
);

export const moicSelector = createSelector(
  selectedFsisDates,
  (_state: RootState, props: CombinedSelectorProps) => props,
  ({ data: fsisDates }, { year, quarter, assetClass, strategy }): MoicMeta => {
    const isQ42025OrAfter = calculateIsQ42025OrAfter(year, quarter);
    const isFundGPEExcludingRealEstate = calculateIsFundGPEExcludingRealEstate(assetClass, strategy);

    if (fsisDates === null) {
      return {
        hasPreviousQuarterMoic: isQ42025OrAfter || quarter !== QuarterValue.First,
        hasPreviousYearMoic: quarter !== QuarterValue.Third,
        previousQuarterDate: null,
        previousYearLastQuarterDate: null,
        previousYearSameQuarterDate: null,
        isQ42025OrAfter,
        isFundGPEExcludingRealEstate,
      };
    }

    const asOfEndDate = DateUtils.convertYearQuarterToEndDate(year, quarter);

    // Starting from Q4 2025, always show previousQuarterMoic (even in Q1) since endPreviousYearMOIC is hidden
    const hasPreviousQuarterMoic = isQ42025OrAfter || quarter !== QuarterValue.First;
    const hasPreviousYearMoic = quarter !== QuarterValue.Fourth;

    return {
      hasPreviousQuarterMoic,
      hasPreviousYearMoic,
      previousQuarterDate: hasPreviousQuarterMoic
        ? format(lastDayOfMonth(subMonths(asOfEndDate, 3)), "MM-dd-yyyy")
        : null,
      previousYearLastQuarterDate: hasPreviousYearMoic
        ? format(lastDayOfYear(subYears(asOfEndDate, 1)), "MM-dd-yyyy")
        : null,
      previousYearSameQuarterDate: format(lastDayOfMonth(subYears(asOfEndDate, 1)), "MM-dd-yyyy"),
      isQ42025OrAfter,
      isFundGPEExcludingRealEstate,
    };
  }
);

const memoizedFormatNumber = memoize(
  (value: number, format: NumberFormat, decimals: number) => NumberUtils.formatNumber(value ?? 0, format, decimals),
  (...args) => args.join("_")
);

const sisGridFormatter = ({
  numberFormat,
  notePositions,
}: {
  numberFormat: NumberFormat;
  notePositions: Record<string, number>;
}): Record<string, Function> => ({
  companyName: (value: string): string | JSX.Element =>
    notePositions[value]
      ? React.createElement("span", null, `${value}\xA0`, React.createElement("sup", null, notePositions[value]))
      : value,
  acquisitionDate: (value: string): string =>
    // @todo Fix backend response: date can be null | string but not "null"
    value && value !== "null" ? format(new Date(value), "MMM-yy") : "",
  equityInvested: (value: number): string => memoizedFormatNumber(value, numberFormat, 1),
  cashReceived: (value: number): string => memoizedFormatNumber(value, numberFormat, 1),
  remainingFmv: (value: number): string => memoizedFormatNumber(value, numberFormat, 1),
  totalValue: (value: number): string => memoizedFormatNumber(value, numberFormat, 1),
  moic: (value: Nullable<number>): string =>
    value === null ? "N/A" : `${memoizedFormatNumber(value, numberFormat, 2)}x`,
  previousQuarterMOIC: (value: number): string =>
    value === null ? "N/A" : `${memoizedFormatNumber(value, numberFormat, 2)}x`,
  endPreviousYearMOIC: (value: number): string =>
    value === null ? "N/A" : `${memoizedFormatNumber(value, numberFormat, 2)}x`,
  sameQuarterPreviousYearMOIC: (value: number): string =>
    value === null ? "N/A" : `${memoizedFormatNumber(value, numberFormat, 2)}x`,
  formattedGrossIRR: (value: string): string =>
    value === null
      ? ""
      : value.toString() === "N/A" || value.toString() === "NM" || value.toString() === "Neg"
      ? value
      : `${memoizedFormatNumber(parseFloat(value), numberFormat, 1)}%`,
});

const formatFsisGridRow = ({
  report,
  numberFormat,
  notePositions,
}: {
  report: Partial<FSISReport> | FsisFlocReport | FsisReportTotal | FPSISSubtotal;
  numberFormat: NumberFormat;
  notePositions: Record<string, number>;
}) => {
  const formatters = sisGridFormatter({ numberFormat, notePositions });

  return Object.entries(report).reduce(
    (result, [key, value]) => ({
      ...result,
      [key]: formatters?.[key]?.(value) ?? value,
    }),
    {}
  );
};

const formatFsisGridRows = ({
  reports,
  numberFormat,
  notePositions,
}: {
  reports: (Partial<FSISReport> | FsisFlocReport | FsisReportTotal | FPSISSubtotal)[];
  numberFormat: NumberFormat;
  notePositions: Record<string, number>;
}) => {
  return reports.map((report) => {
    return formatFsisGridRow({ report, numberFormat, notePositions });
  });
};

export const sisGridDataSelector = createSelector(
  selectedHistoricMoic,
  fsisNotePositionsSelector,
  fsisGroupedSelector,
  selectedFsisDateSelector,
  preferencesSelector,
  (_state: RootState, options: InvestmentScheduleSelectorOptions) => options,
  (
    { data: historicMoic },
    noteMap,
    grouped,
    date,
    { NumberFormat: numberFormat },
    options
  ): ReadonlyArray<FSISReport & BaseRow> => {
    const { fundId, currency, ilmFundGrossIRR, ilmFundNetIRR, ilmFundNetMoic } = options;

    if (date === null || !grouped || noteMap === null) {
      return [];
    }

    const isReportCurrentStateFinal: boolean = `${grouped.investments?.[0]?.currentState}` === "Final";
    const { groupedData, flocData, totals, flags } = grouped;
    const { isQ42025OrAfter, isFundGPEExcludingRealEstate } = flags;

    const notePositions = Object.entries(noteMap).reduce<Record<string, number>>((acc, [key, { position }]) => {
      acc[key] = position;

      return acc;
    }, {});

    const realizedReports = orderBy(
      groupedData.realized.reports,
      (report) => new Date(report.exitDate).valueOf(),
      "asc"
    );
    const unrealizedReports = orderBy(
      groupedData.unrealized.reports,
      (report) => new Date(report.acquisitionDate).valueOf(),
      "asc"
    );
    const publicTradeReports = orderBy(
      groupedData.publiclyTraded.reports,
      (report) => new Date(report.acquisitionDate).valueOf(),
      "asc"
    );
    const adjustmentReports = groupedData.adjustments.reports;
    const realizedTotal = groupedData.realized.subtotal;
    const unrealizedTotal = groupedData.unrealized.subtotal;
    const publicTradeTotal = groupedData.publiclyTraded.subtotal;
    const totalInvestment = totals.totalInvestment;
    const totalFunds = totals.totalFunds;

    return flow(
      (rows) => {
        return realizedReports.length && realizedTotal
          ? [
              ...rows,
              {
                companyName: isQ42025OrAfter ? "Realized/Partially Realized Investments" : "Realized Investments",
                rowMeta: {
                  type: RowType.Divider,
                },
              },
              ...formatFsisGridRows({
                reports: realizedReports.map((report) => ({
                  ...report,
                  rowMeta: {
                    currency,
                  },
                })),
                numberFormat,
                notePositions,
              }),
              {
                ...formatFsisGridRow({
                  report: realizedTotal,
                  numberFormat,
                  notePositions,
                }),
                companyName: isQ42025OrAfter
                  ? "Total Realized/Partially Realized Investments"
                  : "Total Realized Investments",
                rowMeta: {
                  type: RowType.SubHeader,
                  currency,
                },
              },
            ]
          : rows;
      },
      (rows) => {
        return publicTradeReports.length && publicTradeTotal
          ? [
              ...rows,
              {
                companyName: "Publicly Traded Investments",
                rowMeta: {
                  type: RowType.Divider,
                },
              },
              ...formatFsisGridRows({
                reports: publicTradeReports.map((report) => ({
                  ...report,
                  rowMeta: {
                    currency,
                  },
                })),
                numberFormat,
                notePositions,
              }),
              {
                ...formatFsisGridRow({
                  report: publicTradeTotal,
                  numberFormat,
                  notePositions,
                }),
                companyName: "Total Publicly Traded Investments",
                rowMeta: {
                  type: RowType.SubHeader,
                  currency,
                },
              },
            ]
          : rows;
      },
      (rows) => {
        return unrealizedReports.length && unrealizedTotal
          ? [
              ...rows,
              {
                companyName: isQ42025OrAfter ? "Unrealized Investments" : "Unrealized/Partially Realized Investments",
                rowMeta: {
                  type: RowType.Divider,
                },
              },
              ...formatFsisGridRows({
                reports: unrealizedReports.map((report) => ({
                  ...report,
                  rowMeta: {
                    currency,
                  },
                })),
                numberFormat,
                notePositions,
              }),
              {
                ...formatFsisGridRow({
                  report: unrealizedTotal,
                  numberFormat,
                  notePositions,
                }),
                companyName: isQ42025OrAfter
                  ? "Total Unrealized Investments"
                  : "Total Unrealized/Partially Realized Investments",
                rowMeta: {
                  type: RowType.SubHeader,
                  currency,
                },
              },
            ]
          : rows;
      },
      (rows) => {
        return [
          ...rows,
          ...formatFsisGridRows({
            reports: flocData.firstThird.map((report) => ({
              ...report,
              companyName: report.adjustmentTypeValue,
              rowMeta: {
                currency,
              },
            })),
            numberFormat,
            notePositions,
          }),
        ];
      },
      (rows) => {
        return !isEmpty(totalInvestment)
          ? isQ42025OrAfter
            ? [
                ...rows,
                {
                  ...formatFsisGridRow({
                    report: {
                      ...totalInvestment,
                      formattedGrossIRR: isFundGPEExcludingRealEstate ? ilmFundGrossIRR ?? "" : "",
                      previousQuarterMOIC: historicMoic?.[fundId]?.[date]?.previousQuarterMOIC ?? null,
                      sameQuarterPreviousYearMOIC: historicMoic?.[fundId]?.[date]?.sameQuarterPreviousYearMOIC ?? null,
                    },
                    numberFormat,
                    notePositions,
                  }),
                  companyName: "Total Fund",
                  rowMeta: {
                    type: RowType.BoldHeader,
                    currency,
                  },
                },
              ]
            : [
                ...rows,
                {
                  ...formatFsisGridRow({
                    report: {
                      ...totalInvestment,
                      endPreviousYearMOIC: historicMoic?.[fundId]?.[date]?.endPreviousYearMOIC ?? null,
                      previousQuarterMOIC: historicMoic?.[fundId]?.[date]?.previousQuarterMOIC ?? null,
                      sameQuarterPreviousYearMOIC: historicMoic?.[fundId]?.[date]?.sameQuarterPreviousYearMOIC ?? null,
                    },
                    numberFormat,
                    notePositions,
                  }),
                  companyName: "Total Investments",
                  rowMeta: {
                    type: RowType.BoldHeader,
                    currency,
                  },
                },
              ]
          : rows;
      },
      (rows) => {
        return [
          ...rows,
          ...formatFsisGridRows({
            reports: flocData.secondFourth.map((report) => ({
              ...report,
              companyName: report.adjustmentTypeValue,
              rowMeta: {
                type: RowType.SubHeader,
                currency,
              },
            })),
            numberFormat,
            notePositions,
          }),
        ];
      },
      (rows) => {
        return adjustmentReports.length
          ? [
              ...rows,
              ...formatFsisGridRows({
                reports: adjustmentReports.map(
                  ({ moic, previousQuarterMOIC, endPreviousYearMOIC, sameQuarterPreviousYearMOIC, ...report }) => ({
                    ...report,
                    rowMeta: {
                      currency,
                    },
                  })
                ),
                numberFormat,
                notePositions,
              }),
            ]
          : rows;
      },
      (rows) => {
        return totalInvestment && !isEmpty(totalInvestment) && totalFunds && !isEmpty(totalFunds)
          ? isQ42025OrAfter
            ? [
                ...rows,
                {
                  ...formatFsisGridRow({
                    report: totalFunds,
                    numberFormat,
                    notePositions,
                  }),
                  companyName: "Total Fund Adjusted",
                  rowMeta: {
                    type: RowType.SubHeader,
                    currency,
                  },
                },
              ]
            : [
                ...rows,
                {
                  ...formatFsisGridRow({
                    report: totalFunds,
                    numberFormat,
                    notePositions,
                  }),
                  companyName: "Total Fund",
                  rowMeta: {
                    type: RowType.SubHeader,
                    currency,
                  },
                },
              ]
          : rows;
      },
      (rows) => {
        if (!isQ42025OrAfter || !isFundGPEExcludingRealEstate || !isReportCurrentStateFinal) {
          return rows;
        }

        return [
          ...rows,
          {
            companyName: "Total Fund Net Returns",
            moic: React.createElement(
              "span",
              null,
              React.createElement(
                "strong",
                null,
                "Net Multiple",
                " ",
                React.createElement("sup", { style: { fontWeight: "inherit" } }, "6")
              )
            ),
            formattedGrossIRR: React.createElement("span", null, "Net IRR", " ", React.createElement("sup", null, "7")),
            rowMeta: {
              type: RowType.SubHeader,
              currency,
            },
          },
          {
            ...formatFsisGridRow({
              report: {
                moic: ilmFundNetMoic,
                formattedGrossIRR: ilmFundNetIRR,
              },
              numberFormat,
              notePositions,
            }),
            moic:
              ilmFundNetMoic === null || ilmFundNetMoic === ""
                ? "N/A"
                : ilmFundNetMoic === "N/A" || ilmFundNetMoic === "NM" || ilmFundNetMoic === "Neg"
                ? ilmFundNetMoic
                : React.createElement(
                    "strong",
                    null,
                    `${NumberUtils.formatNumber(Number(ilmFundNetMoic ?? 0), numberFormat, 2)}x`
                  ),
            rowMeta: {
              currency,
            },
          },
        ];
      }
    )([]);
  }
);

export const isFundDocumentsDataLoadingSelector = createSelector(
  fundDocumentsSelector,
  (fundDocuments) => fundDocuments.loading
);

export const fundDocumentsDataGridRowsSelector = createSelector(
  fundDocumentsSelector,
  preferencesSelector,
  (fundDocuments, { DateFormat }): Nullable<FundDocumentsRow[]> => {
    if (!fundDocuments.data) {
      return null;
    }

    return fundDocuments.data.map((document) => ({
      id: document.id,
      batchId: document.batchId,
      publishDate:
        document.publishDate && document.documentStatus === DocumentStatus.Active
          ? DateUtils.formatDate(new Date(document.publishDate), DateFormat)
          : "N/A",
      investorAccountName: document.investorAccountName || "All Investors",
      markedRead: document.markedRead,
      actionItem: document.actionItem,
      documentStatus: document.documentStatus,
      title: document.title,
      fileType: document.fileType,
      fundId: document.fundId,
      documentSubtype: document.documentSubtype,
      isLoading: document.isLoading,
      readOnlyFlag: document.readOnlyFlag,
      rowMeta: document.readOnlyFlag
        ? {
            type:
              document.documentStatus === DocumentStatus.Pending ||
              document.documentStatus === DocumentStatus.PendingApproval
                ? RowType.HighlightedReadonly
                : RowType.Readonly,
            cellRenderers: {
              download: () => null,
            },
          }
        : {
            type:
              document.documentStatus === DocumentStatus.Pending ||
              document.documentStatus === DocumentStatus.PendingApproval
                ? RowType.Highlighted
                : RowType.Regular,
          },
    }));
  }
);

export const investmentScheduleSelector = createSelector(
  selectedFsisDates,
  selectedFsisGrouped,
  selectedFsisDateSelector,
  sortedFsisDateOptionsSelector,
  selectedHistoricMoic,
  latestFsisAsOfDateOptionSelector,
  selectedSummaryInvestmentScheduleFilters,
  (state: RootState, options: InvestmentScheduleSelectorOptions) => selectedFsisReportSelector(state, options.fundId),
  (state: RootState, options: InvestmentScheduleSelectorOptions) => sisGridDataSelector(state, options),
  (state: RootState, options: InvestmentScheduleSelectorOptions) =>
    selectedFsisReportSelector(state, options.assetClass),
  (state: RootState, options: InvestmentScheduleSelectorOptions) => selectedFsisReportSelector(state, options.strategy),
  (state: RootState, options: InvestmentScheduleSelectorOptions) =>
    selectedFsisReportSelector(state, options.ilmFundGrossIRR ?? ""),
  (state: RootState, options: InvestmentScheduleSelectorOptions) =>
    selectedFsisReportSelector(state, options.ilmFundNetIRR ?? ""),
  (state: RootState, options: InvestmentScheduleSelectorOptions) =>
    selectedFsisReportSelector(state, options.ilmFundNetMoic ?? ""),
  (
    fsisDates,
    fsisGrouped,
    selectedFsisDate,
    fsisDatesOptions,
    historicMoic,
    latestFsisAsOfDate,
    filters,
    selectedFsisReport,
    gridData,
    assetClass,
    strategy,
    ilmFundGrossIRR,
    ilmFundNetIRR,
    ilmFundNetMoic
  ) => ({
    fsisDates,
    fsisGrouped,
    selectedFsisDate,
    fsisDatesOptions,
    historicMoic,
    latestFsisAsOfDate,
    filters,
    selectedFsisReport,
    gridData,
    assetClass,
    strategy,
    ilmFundGrossIRR,
    ilmFundNetIRR,
    ilmFundNetMoic,
  })
);
